import React, { useState, useEffect } from 'react';
import './Carousel.css';

interface CarouselItem {
  id: string;
  title: string;
  body: string;
  design: 'primary' | 'secondary' | 'success' | 'info' | 'light';
  order: number;
  enabled: boolean;
}

interface CarouselProps {
  items: CarouselItem[];
  autoPlayInterval?: number;
}

const Carousel: React.FC<CarouselProps> = ({ items, autoPlayInterval = 5000 }) => {
  const [currentIndex, setCurrentIndex] = useState(0);
  const [lastInteraction, setLastInteraction] = useState(Date.now());

  // Filter only enabled items and sort by order
  const activeItems = items
    .filter(item => item.enabled)
    .sort((a, b) => a.order - b.order);

  useEffect(() => {
    if (activeItems.length === 0) return;

    const interval = setInterval(() => {
      setCurrentIndex((prevIndex) => (prevIndex + 1) % activeItems.length);
    }, autoPlayInterval);

    return () => clearInterval(interval);
  }, [activeItems.length, autoPlayInterval, lastInteraction]);

  const goToSlide = (index: number) => {
    setCurrentIndex(index);
    setLastInteraction(Date.now()); // Reset timer
  };

  const goToPrevious = () => {
    setCurrentIndex((prevIndex) => 
      prevIndex === 0 ? activeItems.length - 1 : prevIndex - 1
    );
    setLastInteraction(Date.now()); // Reset timer
  };

  const goToNext = () => {
    setCurrentIndex((prevIndex) => (prevIndex + 1) % activeItems.length);
    setLastInteraction(Date.now()); // Reset timer
  };

  if (activeItems.length === 0) {
    return null;
  }

  return (
    <div className="carousel">
      <div className="carousel-container">
        <button 
          className="carousel-button carousel-button-prev" 
          onClick={goToPrevious}
          aria-label="Forrige"
        >
          ‹
        </button>

        <div className="carousel-content">
          {activeItems.map((item, index) => (
            <div
              key={item.id}
              className={`carousel-item carousel-item-${item.design} ${
                index === currentIndex ? 'active' : ''
              }`}
            >
              <div className="carousel-item-scroll">
                <h3 className="carousel-title">{item.title}</h3>
                <p className="carousel-body">{item.body}</p>
              </div>
            </div>
          ))}
        </div>

        <button 
          className="carousel-button carousel-button-next" 
          onClick={goToNext}
          aria-label="Neste"
        >
          ›
        </button>
      </div>

      <div className="carousel-indicators">
        {activeItems.map((_, index) => (
          <button
            key={index}
            className={`carousel-indicator ${index === currentIndex ? 'active' : ''}`}
            onClick={() => goToSlide(index)}
            aria-label={`Gå til slide ${index + 1}`}
          />
        ))}
      </div>
    </div>
  );
};

export default Carousel;
